import { useEffect, useMemo, useState } from "react";
import "../../../assets/css/loanReports.css";

type LoanReportRow = {
  id: number;
  loan_account: string;
  customer_name: string;
  assignment_number: string;
  product: string;
  loan_amount: number;
  outstanding_balance: number;
  total_paid: number;
  next_due_date: string | null;
  days_overdue: number;
  status: "PERFORMING" | "UNPAID" | "OVERDUE" | "BAD_LOAN" | "CLOSED";
};

export default function LoanReports() {
  const [loans, setLoans] = useState<LoanReportRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [product, setProduct] = useState("ALL");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchReports = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE}/loan_reports.php`,
        { credentials: "include" }
      );

      const data = await res.json();

      if (data.success) {
        setLoans(data.loans || []);
      } else {
        setLoans([]);
      }
    } catch (err) {
      console.error("Loan reports error:", err);
      setLoans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, status, product]);

  const products = useMemo(() => {
    return Array.from(new Set(loans.map((l) => l.product).filter(Boolean)));
  }, [loans]);

  const filteredLoans = loans.filter((loan) => {
    const q = search.toLowerCase();

    const matchesSearch =
      loan.customer_name?.toLowerCase().includes(q) ||
      loan.loan_account?.toLowerCase().includes(q) ||
      loan.assignment_number?.toLowerCase().includes(q) ||
      loan.product?.toLowerCase().includes(q);

    const matchesStatus = status === "ALL" || loan.status === status;
    const matchesProduct = product === "ALL" || loan.product === product;

    return matchesSearch && matchesStatus && matchesProduct;
  });

  const totalPages = Math.ceil(filteredLoans.length / itemsPerPage) || 1;

  const paginatedLoans = filteredLoans.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const summary = {
    totalPortfolio: loans.reduce((sum, l) => sum + Number(l.loan_amount || 0), 0),
    outstanding: loans.reduce((sum, l) => sum + Number(l.outstanding_balance || 0), 0),
    totalPaid: loans.reduce((sum, l) => sum + Number(l.total_paid || 0), 0),
    performing: loans.filter((l) => l.status === "PERFORMING").length,
    unpaid: loans.filter((l) => l.status === "UNPAID").length,
    overdue: loans.filter((l) => l.status === "OVERDUE").length,
    badLoans: loans.filter((l) => l.status === "BAD_LOAN").length,
  };

  const formatMoney = (amount: number) =>
    `₦${Number(amount || 0).toLocaleString()}`;

  const downloadReport = (type: string) => {
    window.open(
      `${process.env.REACT_APP_API_BASE}/download_loan_report.php?type=${type}`,
      "_blank"
    );
  };

  return (
    <div className="loan-reports-page">
      <div className="loan-reports-header">
        <div>
          <span className="loan-reports-badge">Loan Intelligence</span>
          <h2>Loan Reports Dashboard</h2>
          <p>
            Monitor performing loans, unpaid loans, overdue accounts, bad loans and portfolio quality.
          </p>
        </div>

        <button className="loan-refresh-btn" onClick={fetchReports}>
          <i className="fa fa-refresh" /> Refresh
        </button>
      </div>

      <div className="loan-report-summary-grid">
        <SummaryCard icon="fa fa-briefcase" title="Total Portfolio" value={formatMoney(summary.totalPortfolio)} color="blue" />
        <SummaryCard icon="fa fa-chart-line" title="Total Outstanding" value={formatMoney(summary.outstanding)} color="purple" />
        <SummaryCard icon="fa fa-wallet" title="Total Paid" value={formatMoney(summary.totalPaid)} color="green" />
        <SummaryCard icon="fa fa-check-circle" title="Performing Loans" value={summary.performing} color="green" />
        <SummaryCard icon="fa fa-clock" title="Unpaid Loans" value={summary.unpaid} color="orange" />
        <SummaryCard icon="fa fa-exclamation-triangle" title="Overdue Loans" value={summary.overdue} color="red" />
        <SummaryCard icon="fa fa-ban" title="Bad Loans" value={summary.badLoans} color="red" />
      </div>

      <div className="loan-report-card">
        <div className="loan-report-card-header">
          <div>
            <h4>Loan Portfolio Report</h4>
            <p>Detailed breakdown of loan performance and repayment position.</p>
          </div>

          <div className="loan-report-downloads">
            <button onClick={() => downloadReport("all")}>Export All</button>
            <button onClick={() => downloadReport("performing")}>Performing</button>
            <button onClick={() => downloadReport("overdue")}>Overdue</button>
            <button onClick={() => downloadReport("bad_loans")}>Bad Loans</button>
          </div>
        </div>

        <div className="loan-report-filters">
          <div className="loan-report-search">
            <i className="fa fa-search" />
            <input
              placeholder="Search customer, loan account, IPPIS, product..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ALL">All Status</option>
            <option value="PERFORMING">Performing</option>
            <option value="UNPAID">Unpaid</option>
            <option value="OVERDUE">Overdue</option>
            <option value="BAD_LOAN">Bad Loan</option>
            <option value="CLOSED">Closed</option>
          </select>

          <select value={product} onChange={(e) => setProduct(e.target.value)}>
            <option value="ALL">All Products</option>
            {products.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="loan-report-pagination">
          <span>
            Showing{" "}
            {filteredLoans.length === 0
              ? 0
              : (currentPage - 1) * itemsPerPage + 1}
            –{Math.min(currentPage * itemsPerPage, filteredLoans.length)} of{" "}
            {filteredLoans.length}
          </span>

          <div className="loan-report-pagination-controls">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>

            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              ‹
            </button>

            <strong>{currentPage}</strong>

            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              ›
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="loan-report-table">
            <thead>
              <tr>
                <th>S/N</th>
                <th>Customer</th>
                <th>Loan Account</th>
                <th>Product</th>
                <th>Loan Amount</th>
                <th>Total Paid</th>
                <th>Outstanding</th>
                <th>Next Due Date</th>
                <th>Days Overdue</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="loan-report-empty">
                    Loading loan reports...
                  </td>
                </tr>
              ) : filteredLoans.length === 0 ? (
                <tr>
                  <td colSpan={10} className="loan-report-empty">
                    <i className="fa fa-folder-open" />
                    <p>No loan report records found</p>
                    <small>Reports will appear here once loans are available.</small>
                  </td>
                </tr>
              ) : (
                paginatedLoans.map((loan, index) => (
                  <tr key={loan.id}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>
                      <strong>{loan.customer_name}</strong>
                      <span>{loan.assignment_number || "-"}</span>
                    </td>
                    <td>{loan.loan_account || "-"}</td>
                    <td>{loan.product || "-"}</td>
                    <td>{formatMoney(loan.loan_amount)}</td>
                    <td className="text-success">{formatMoney(loan.total_paid)}</td>
                    <td className="text-danger">{formatMoney(loan.outstanding_balance)}</td>
                    <td>{loan.next_due_date || "-"}</td>
                    <td>{loan.days_overdue || 0}</td>
                    <td>
                      <span className={`loan-report-status ${loan.status.toLowerCase()}`}>
                        {loan.status.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  title,
  value,
  color,
}: {
  icon: string;
  title: string;
  value: string | number;
  color: "blue" | "purple" | "green" | "orange" | "red";
}) {
  return (
    <div className={`loan-report-summary-card ${color}`}>
      <div className="loan-report-card-icon">
        <i className={icon}></i>
      </div>

      <div>
        <span>{title}</span>
        <h3>{value}</h3>
      </div>
    </div>
  );
}